import { MESSAGE_RESOURCE, DOWNLOAD_FAILED_MAP, DOWNLOAD_SUCCESS_MAP, OPERATE_STATUS, DOWNLOAD_CENTER_BUTTON_TYPE_META } from '@common/oneclub/constants'
import type { DownloadItemType, MessageItemType } from '@common/oneclub/store/notificationSlice/type'
import { cloneDeep } from 'lodash'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from '@common/oneclub/store'
import { useIsH5 } from '../components/Device'
import { formatPeriod, stringifyQueryEncode, translateCurrency } from '../utils'
import { useHistory } from 'react-router-dom'
import { useUpdateDefaultWallet } from './useUpdateDefaultWallet'
import { creditTypes, changeTypes } from '@common/oneclub/constants/oneStar'
import { ValueOf } from '../typings'
// import { useCaptcha } from './useCaptcha'

export interface MessageInfo {
  /** 站内信标题 */
  title: string
  /**站内信内容第一行 */
  content: string
  /** 站内信标题前缀 */
  suffix?: string
  /** 站内信内容第二行（某些站内信有两行内容） */
  content1?: string
}

interface BtnConfigsType {
  btnText: string
  onClick: (p: any, token?: string) => Promise<void>
  show: string | boolean
  buttonType: ValueOf<typeof DOWNLOAD_CENTER_BUTTON_TYPE_META>
}

export type StatusTypes = 'success' | 'normal' | 'fail'

interface Params {
  messageItem: MessageItemType
  onClose?: () => void
  isH5?: boolean
}

export function useBtnConfigs() {
  const history = useHistory()
  const { t: originT } = useTranslation()

  const updateDefalutWallet = useUpdateDefaultWallet()

  const getBtnConfigs = (item: MessageItemType, params?: { onClose?: () => void }) => {
    // 统一注入extInfo
    const t = (key: string, otherInfo?: Record<string, string>) => originT(key, { ...item.extInfo, ...otherInfo })

    return [
      {
        show: item.msgSource === MESSAGE_RESOURCE.adjustAccount,
        btnText: t('common.viewDetails'),
        onClick() {
          updateDefalutWallet(item.extInfo.currencyCode)
          history.push('/admin/wallet')
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.COD_BILL_CHECK_OUT,
        btnText: t('common.billsBtn'),
        onClick() {
          history.push(`/admin/billManagement/codBill?status=HAD_CHECK_OUT`)
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.OS_FREIGHT_BILL_PAY,
        btnText: t('common.billsBtn'),
        onClick() {
          history.push(`/admin/billManagement/freight-bill?productLine=OneShip&payStatusIn=already_paid_off`)
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.OW_FREIGHT_BILL_PAY,
        btnText: t('common.billsBtn'),
        onClick() {
          history.push(`/admin/billManagement/freight-bill?productLine=OneWarehouse&payStatusIn=already_paid_off`)
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.OW_STORAGE_BILL_PAY,
        btnText: t('common.billsBtn'),
        onClick() {
          history.push(`/admin/billManagement/owBill?statusList=already_paid_off`)
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.OW_STORAGE_BILL_PUSH,
        btnText: t('common.billsBtn'),
        onClick() {
          history.push(
            stringifyQueryEncode('/admin/billManagement/owBill', {
              timeType: '1',
              timeRange: `${item.extInfo.periodStartTime},${item.extInfo.periodEndTime - 1}`,
              warehouseList: item.extInfo.warehouseId,
            })
          )
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.SUMMARY_BILL_PAY_RESULT,
        btnText: t('common.billsBtn'),
        onClick() {
          history.push(`/admin/billManagement/payBill`)
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.AR_REMIND, // AR追缴费
        btnText: t('go.billManagement'),
        onClick() {
          history.push(`/admin/billManagement/summaryBill`)
        },
      },
      {
        show: item.msgSource === MESSAGE_RESOURCE.DISABLE_PERMISSION, // AR追缴费关闭权限
        btnText: t('go.billManagement'),
        onClick() {
          history.push(`/admin/billManagement/summaryBill`)
        },
      },
    ]
      .filter((item) => {
        return item.show
      })
      .map((item) => ({
        ...item,
        onClick: () => {
          item.onClick?.()
          params?.onClose?.()
        },
      }))
  }

  return getBtnConfigs
}
/**
 * 消息中心信息展示
 */
export function useGetMessageInfo({ messageItem, onClose, isH5 }: Params) {
  const { t: originT } = useTranslation()
  const { countryCode, nickname } = useSelector((state) => state.auth.userInfo)
  const languageType = useSelector((state) => state.language.languageType)
  const getBtnConfigs = useBtnConfigs()

  // 格式化一下
  const item = useMemo(() => {
    const formatItem = cloneDeep(messageItem)
    if (!formatItem?.extInfo) {
      formatItem.extInfo = {}
    }
    try {
      formatItem.extInfo = {
        ...formatItem.extInfo,
        ...JSON.parse(formatItem.msgBody),
      }
    } catch (e) {
      // do nothing
    }
    return formatItem
  }, [messageItem])

  // 统一注入extInfo
  const t = useCallback(
    (key: string, otherInfo?: Record<string, string>) =>
      originT(key, {
        ...item.extInfo,
        ...otherInfo,
      }),
    [item.extInfo, originT]
  )

  /** 消息状态 */
  const messageStatus = useMemo<StatusTypes>(() => {
    if (item.extInfo?.status === OPERATE_STATUS.success) {
      return 'success'
    }

    if (item.extInfo?.status === OPERATE_STATUS.fail) {
      return 'fail'
    }

    return 'success'
  }, [item.extInfo?.status])

  // 消息中心通知
  const messageInfo = useMemo<MessageInfo>(() => {
    // 充值到账提醒
    if (item.msgSource === MESSAGE_RESOURCE.walletDeposit) {
      return {
        title: t('message.rechargeTitle'),
        content: t('message.rechargeContent'),
        suffix: t(`wallet.account${countryCode}`),
      }
    }

    if (item.msgSource === MESSAGE_RESOURCE.walletDepositFail) {
      return {
        title: t('message.rechargeTitle.failed'),
        content: t('message.rechargeContent.failed'),
        suffix: t(`wallet.account${countryCode}`),
      }
    }

    if (item.msgSource === MESSAGE_RESOURCE.adjustAccount) {
      const infos = {
        amount: translateCurrency(Math.abs(item.extInfo.amount)),
        afterdealTotalBalance: translateCurrency(item.extInfo.afterdealTotalBalance),
      }
      let content = item.extInfo?.amount > 0 ? t('adjust.account.content.add', infos) : t('adjust.account.content.sub', infos)
      if (isH5) {
        content = item.extInfo?.amount > 0 ? t('adjust.account.contentH5.add', infos) : t('adjust.account.contentH5.sub', infos)
      }
      return {
        title: t('adjust.account.title', {
          currencyCountryCode: originT(`countryCode.${item.extInfo.currencyCountryCode}`, ''),
        }),
        content,
      }
    }

    // 积分变动通知
    if (item.msgSource === MESSAGE_RESOURCE.SELLER_CREDIT_CHANGED) {
      const creditType = creditTypes.find((i) => i.value === item.extInfo.creditType)
      const changeType = changeTypes.find((i) => i.value === item.extInfo.changeType)
      return {
        title: t('onestar.message.title'),
        content: t('onestar.message.desc', {
          creditType: t(creditType?.label),
          changeType: t(changeType?.label),
          amount: item.extInfo?.amount.toString(),
          creditDetailId: item.extInfo?.creditDetailId,
        }),
      }
    }

    // 仓储账单支付通知
    if (item.msgSource === MESSAGE_RESOURCE.OW_STORAGE_BILL_PAY) {
      return {
        title: t(`message.title.OW_STORAGE_BILL_PAY`),
        content: t(`message.content.OW_STORAGE_BILL_PAY`, {
          period: formatPeriod(item.extInfo),
        }),
      }
    }

    if (item.msgSource === MESSAGE_RESOURCE.OW_STORAGE_BILL_PUSH) {
      const warehouseNames = JSON.parse(item.extInfo.warehouseNames || '{}')
      return {
        title: t(`message.title.OW_STORAGE_BILL_PUSH`),
        content: t(`message.content.OW_STORAGE_BILL_PUSH`, {
          period: formatPeriod(item.extInfo),
          warehouse: warehouseNames?.[languageType],
        }),
      }
    }

    if (item.msgSource === MESSAGE_RESOURCE.SUMMARY_BILL_PAY_RESULT) {
      return {
        title: t(`message.title.SUMMARY_BILL_PAY_RESULT`),
        content:
          item.extInfo?.status === OPERATE_STATUS.success
            ? t(`message.content.SUMMARY_BILL_PAY_RESULT`, {
                period: formatPeriod(item.extInfo),
              })
            : t(`message.content.SUMMARY_BILL_PAY_RESULT.fail`, {
                period: formatPeriod(item.extInfo),
              }),
      }
    }

    if (item.msgSource === MESSAGE_RESOURCE.AR_REMIND) {
      return {
        title: t('message.title.AR_REMIND'),
        content: t('message.content.AR_REMIND', { nickname }),
      }
    }

    if (item.msgSource === MESSAGE_RESOURCE.DISABLE_PERMISSION) {
      return {
        title: t('message.title.DISABLE_PERMISSION'),
        content: t('message.content.DISABLE_PERMISSION', { nickname }),
      }
    }

    return {
      title: t(`message.title.${item.msgSource}`),
      content: t(`message.content.${item.msgSource}`),
    }
  }, [item.msgSource, item.extInfo, t, countryCode, isH5, originT, languageType, nickname])

  return {
    messageInfo,
    messageStatus,
    btnConfigs: getBtnConfigs(item, { onClose }),
  }
}

interface ListItemType {
  onCheck?: () => void
}

/** 改变国家地区需要的信息 */
export interface ChangeAreaType {
  area: string
  data: any
}

export type IlistMessageItemType = ListItemType & {
  item: MessageItemType
}

export type IlistDownloadItemType = ListItemType & {
  item: DownloadItemType
}
/**
 * 下载中心消息展示
 */
export function useGetDownLoadInfo({ item }: IlistDownloadItemType) {
  const { t: originT } = useTranslation()
  // const { validateDownloadUrl } = useCaptcha()

  // 统一注入item信息
  const t = useCallback((key: string) => originT(key, item), [item, originT])

  /** 消息状态 */
  const messageStatus = useMemo<StatusTypes>(() => {
    if ([1, 4].includes(item.fileStatus)) {
      return 'normal'
    }

    if (item.fileStatus === 2) {
      return 'success'
    }

    if (item.fileStatus === 3) {
      return 'fail'
    }
  }, [item.fileStatus])

  const messageInfo = useMemo<MessageInfo>(() => {
    if (item.fileStatus === 2) {
      const conf = DOWNLOAD_SUCCESS_MAP[item.taskType] || DOWNLOAD_SUCCESS_MAP.common
      return {
        title: t(conf.title),
        content: t(conf.content),
      }
    }

    if (item.fileStatus === 3) {
      return {
        title: t(DOWNLOAD_FAILED_MAP[item.taskType]?.title),
        content: t(DOWNLOAD_FAILED_MAP[item.taskType]?.content),
      }
    }

    return {
      title: '',
      content: '',
    }
  }, [item.fileStatus, item.taskType, t])

  // 解决ios window.open 不生效问题
  const isH5 = useIsH5()
  const jumpToSomeWhere = useCallback((url) => {
    const targetGun = document.createElement('a')
    targetGun.href = url
    targetGun.click()
  }, [])

  const handleDownload = useCallback(
    async (e: React.MouseEvent) => {
      e && e.stopPropagation()
      // const { url } = await validateDownloadUrl({ taskId: item.taskId, url: item.presignedUrl, token, captchaScene: CAPTCHA_SCENE.DOWNLOAD_CENTER })
      const url = item.presignedUrl
      if (url) {
        if (isH5) {
          jumpToSomeWhere(url)
        } else {
          window.open(url)
        }
      }
    },
    [isH5, item.presignedUrl, jumpToSomeWhere]
  )

  /** 操作按钮 */
  const btnConfigs = useMemo<BtnConfigsType[]>(() => {
    return [
      {
        btnText: t('download.button'),
        onClick: handleDownload,
        show: item.presignedUrl,
        /** 加个类型，防止后面有新的情况混了 */
        buttonType: DOWNLOAD_CENTER_BUTTON_TYPE_META.DOWNLOAD,
      },
    ].filter((item) => item.show)
  }, [handleDownload, item.presignedUrl, t])

  return {
    messageInfo,
    messageStatus,
    btnConfigs,
  }
}
